// Bento / Multi-brand landing page — monolithic iframe block
// 4 brand variants: noir / atelier / roast / volt
// Uses data-theme attribute on <html> element

export type BentoLandingBrand = 'noir' | 'atelier' | 'roast' | 'volt'
export interface BentoLandingPageData { brand: BentoLandingBrand }
export const DEFAULT_BENTO_LANDING_DATA: BentoLandingPageData = { brand: 'noir' }

// ── CSS ───────────────────────────────────────────────────────────────────────
const BENTO_LANDING_CSS = `
/* ============ BST BENTO ENGINE · 4 BRANDS / 1 CODE ============ */
*{margin:0;padding:0;box-sizing:border-box}
html{scroll-behavior:smooth}
:root{--ez:cubic-bezier(.16,1,.3,1)}

/* ---------- THEME TOKENS ---------- */
[data-theme="noir"]{
  --bg:#07080a; --card:#0e1114; --card2:#13171b; --ink:#f2f4f6; --muted:#8d97a5;
  --accent:#c8f542; --accent-ink:#0b0d05;
  --bd:1px solid rgba(255,255,255,.09); --line:rgba(255,255,255,.14);
  --radius:16px; --gap:14px; --btnr:999px;
  --fd:'Unbounded'; --fb:'Space Grotesk'; --fm:'IBM Plex Mono';
  --shadow:0 24px 70px rgba(0,0,0,.45);
  --hdr-sh:0 14px 44px rgba(0,0,0,.5);
  --bgfx:radial-gradient(1100px 560px at 82% -10%,rgba(200,245,66,.10),transparent 60%),
         radial-gradient(900px 500px at 0% 110%,rgba(200,245,66,.05),transparent 60%),
         repeating-linear-gradient(0deg,rgba(255,255,255,.028) 0 1px,transparent 1px 76px),
         repeating-linear-gradient(90deg,rgba(255,255,255,.028) 0 1px,transparent 1px 76px);
}
[data-theme="atelier"]{
  --bg:#f1ece2; --card:#f9f5ed; --card2:#efe8db; --ink:#191613; --muted:#7c7365;
  --accent:#b0512b; --accent-ink:#fff8f2;
  --bd:1px solid rgba(25,22,19,.18); --line:rgba(25,22,19,.25);
  --radius:4px; --gap:12px; --btnr:999px;
  --fd:'Fraunces'; --fb:'Space Grotesk'; --fm:'IBM Plex Mono';
  --shadow:0 20px 50px rgba(60,40,20,.10);
  --hdr-sh:0 14px 44px rgba(60,40,20,.14);
  --bgfx:radial-gradient(1000px 520px at 92% -4%,rgba(176,81,43,.09),transparent 60%),
         radial-gradient(700px 420px at -10% 100%,rgba(176,81,43,.06),transparent 60%);
}
[data-theme="roast"]{
  --bg:#180f08; --card:#221610; --card2:#2b1c13; --ink:#f7ead9; --muted:#b39b83;
  --accent:#ff8a3d; --accent-ink:#241005;
  --bd:1px solid rgba(255,138,61,.17); --line:rgba(255,138,61,.35);
  --radius:26px; --gap:16px; --btnr:999px;
  --fd:'Bricolage Grotesque'; --fb:'Space Grotesk'; --fm:'IBM Plex Mono';
  --shadow:0 24px 60px rgba(0,0,0,.42);
  --hdr-sh:0 14px 44px rgba(0,0,0,.5);
  --bgfx:radial-gradient(900px 500px at 12% -6%,rgba(255,138,61,.14),transparent 60%),
         radial-gradient(760px 420px at 104% 104%,rgba(255,138,61,.08),transparent 60%);
}
[data-theme="volt"]{
  --bg:#efede8; --card:#fbfaf7; --card2:#f0eee7; --ink:#101014; --muted:#5d5d62;
  --accent:#e42313; --accent-ink:#ffffff;
  --bd:2px solid #101014; --line:rgba(16,16,20,.4);
  --radius:6px; --gap:16px; --btnr:0px;
  --fd:'Anton'; --fb:'Space Grotesk'; --fm:'IBM Plex Mono';
  --shadow:8px 8px 0 #101014;
  --hdr-sh:6px 6px 0 rgba(16,16,20,.9);
  --bgfx:repeating-linear-gradient(135deg,rgba(16,16,20,.05) 0 2px,transparent 2px 28px),
         radial-gradient(820px 420px at 100% 0%,rgba(228,35,19,.08),transparent 60%);
}

body{
  background:var(--bg); color:var(--ink);
  font-family:var(--fb),system-ui,sans-serif; line-height:1.55;
  overflow-x:hidden; transition:background-color .7s var(--ez),color .7s var(--ez);
}
body.menu-open{overflow:hidden}
body::before{content:"";position:fixed;inset:0;background:var(--bgfx);pointer-events:none;z-index:0;transition:opacity .7s}
body::after{content:"";position:fixed;inset:0;pointer-events:none;z-index:80;opacity:.05;
  background:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='160' height='160' filter='url(%23n)' opacity='0.5'/%3E%3C/svg%3E");}
::selection{background:var(--accent);color:var(--accent-ink)}
::-webkit-scrollbar{width:10px}::-webkit-scrollbar-thumb{background:var(--line);border-radius:99px}::-webkit-scrollbar-track{background:transparent}
a{color:inherit;text-decoration:none}
button{font:inherit;color:inherit;background:none;border:none;cursor:pointer}
:focus-visible{outline:2px solid var(--accent);outline-offset:3px;border-radius:4px}
img{display:block;max-width:100%}
sup{font-size:.55em}
.fm{font-family:var(--fm),monospace}
em{font-style:normal;color:var(--accent)}
[data-theme="atelier"] em,[data-theme="roast"] em{font-style:italic}
[data-theme="volt"] em{display:inline-block;transform:skewX(-8deg)}
[data-theme="noir"] .hero-title em{text-shadow:0 0 28px rgba(200,245,66,.45)}
#manifest,#spectrum,#contact{scroll-margin-top:110px}

/* ---------- CURSOR ---------- */
#cur{position:fixed;left:0;top:0;width:14px;height:14px;border-radius:50%;background:#fff;mix-blend-mode:difference;pointer-events:none;z-index:99;transform:translate(-50%,-50%);transition:width .3s,height .3s;opacity:0}
#cur.on{opacity:1}#cur.big{width:52px;height:52px}
@media(pointer:coarse){#cur{display:none}}

/* ---------- HEADER ---------- */
.hdr{position:fixed;top:0;left:0;right:0;z-index:70;
  backdrop-filter:blur(18px) saturate(1.3);-webkit-backdrop-filter:blur(18px) saturate(1.3);
  background:color-mix(in srgb,var(--bg) 82%,transparent);
  border-bottom:var(--bd);
  transition:background-color .5s var(--ez),border-color .7s,box-shadow .5s var(--ez)}
.hdr.scrolled{background:color-mix(in srgb,var(--bg) 96%,transparent);box-shadow:var(--hdr-sh)}
.hdr-in{max-width:1440px;margin:0 auto;padding:14px 28px;display:flex;align-items:center;gap:22px;min-height:64px;transition:padding .4s var(--ez),min-height .4s var(--ez)}
.hdr.scrolled .hdr-in{padding-top:9px;padding-bottom:9px;min-height:56px}
.brand{display:flex;align-items:center;gap:10px;flex:none}
.mark{width:36px;height:36px;background:var(--accent);color:var(--accent-ink);border-radius:9px;display:grid;place-items:center;
  font-family:var(--fd);font-weight:800;font-size:10px;letter-spacing:.02em;transition:background-color .7s,border-radius .7s,color .7s,width .3s,height .3s}
.hdr.scrolled .mark{width:32px;height:32px}
[data-theme="volt"] .mark{border-radius:0}
.brand-t{font-weight:500;font-size:13px;letter-spacing:.14em}
.dnav{display:flex;gap:22px;margin-left:8px;font-size:11px;letter-spacing:.14em;text-transform:uppercase;flex:none}
.dnav a{color:var(--muted);position:relative;padding:4px 0;transition:color .3s}
.dnav a::after{content:"";position:absolute;left:0;bottom:0;height:1px;width:0;background:var(--accent);transition:width .35s var(--ez)}
.dnav a:hover{color:var(--ink)}.dnav a:hover::after{width:100%}
.hdr-r{margin-left:auto;display:flex;align-items:center;gap:14px;flex:none}
.sw-lab{font-size:10px;letter-spacing:.18em;color:var(--muted);white-space:nowrap}

.switcher{position:relative;display:grid;grid-template-columns:repeat(4,1fr);border:var(--bd);border-radius:999px;padding:4px;background:var(--card);transition:background-color .7s,border-color .7s;flex:none}
.sw-ind{position:absolute;top:4px;bottom:4px;left:4px;width:calc(25% - 6px);background:var(--accent);border-radius:999px;transition:transform .5s var(--ez),background-color .7s}
.sw-btn{position:relative;z-index:1;display:flex;align-items:center;justify-content:center;gap:7px;padding:7px 14px;font-size:10px;letter-spacing:.14em;text-transform:uppercase;color:var(--muted);transition:color .4s;border-radius:999px;min-width:0;white-space:nowrap}
.sw-btn i{width:7px;height:7px;border-radius:50%;background:var(--c);flex:none;transition:transform .3s}
.sw-btn.on{color:var(--accent-ink)}
.sw-btn.on i{background:var(--accent-ink);transform:scale(1.25)}

.btn{display:inline-flex;align-items:center;justify-content:center;gap:10px;padding:14px 24px;border-radius:var(--btnr);font-family:var(--fm);font-size:12px;letter-spacing:.1em;text-transform:uppercase;transition:transform .35s var(--ez),box-shadow .35s var(--ez),background-color .5s,color .5s,border-radius .7s;will-change:transform}
.btn-p{background:var(--accent);color:var(--accent-ink)}
.btn-p svg{transition:transform .35s var(--ez)}
.btn-p:hover{transform:translateY(-3px)}
.btn-p:hover svg{transform:translateX(5px)}
.btn-g{border:var(--bd);color:var(--ink)}
.btn-g:hover{border-color:var(--accent);color:var(--accent);transform:translateY(-3px)}
[data-theme="volt"] .btn-p{box-shadow:5px 5px 0 var(--ink)}
[data-theme="volt"] .btn-p:hover{transform:translate(-2px,-2px);box-shadow:8px 8px 0 var(--ink)}
.btn-s{padding:10px 18px}

.burger{display:none;flex-direction:column;gap:6px;padding:8px;z-index:2;flex:none}
.burger span{width:26px;height:2px;background:var(--ink);transition:transform .4s var(--ez),opacity .3s}
body.menu-open .burger span:first-child{transform:translateY(4px) rotate(45deg)}
body.menu-open .burger span:last-child{transform:translateY(-4px) rotate(-45deg)}
.mnav{position:fixed;inset:0;background:var(--bg);z-index:60;display:flex;flex-direction:column;justify-content:center;padding:0 7vw;gap:6px;transform:translateY(-102%);transition:transform .6s var(--ez)}
body.menu-open .mnav{transform:none}
.mnav a{font-family:var(--fd);font-size:clamp(1.7rem,7vw,3.2rem);color:var(--ink);border-bottom:var(--bd);padding:14px 0;display:flex;justify-content:space-between;align-items:center;min-width:0}
.mnav a small{font-family:var(--fm);font-size:11px;color:var(--muted);flex:none}

/* ---------- LAYOUT ---------- */
.wrap{max-width:1440px;margin:0 auto;padding:0 28px;position:relative;z-index:1}
/* отступ под фиксированную шапку: main.wrap перебивает .wrap по специфичности */
main.wrap{padding-top:132px}
.bento{display:grid;gap:var(--gap)}
.cell{position:relative;background:var(--card);border:var(--bd);border-radius:var(--radius);box-shadow:var(--shadow);
  padding:26px;overflow:hidden;
  transition:transform .45s var(--ez),box-shadow .45s var(--ez),background-color .7s,border-color .7s,border-radius .7s}
.cell:hover{transform:translateY(-5px)}
[data-theme="volt"] .cell:hover{transform:translate(-3px,-3px);box-shadow:11px 11px 0 var(--ink)}
.cell[data-i]::after{content:attr(data-i);position:absolute;top:16px;right:18px;font-family:var(--fm);font-size:10px;letter-spacing:.14em;color:var(--muted);opacity:.7}

/* reveal */
.rv{opacity:0}
.rv.in{animation:rise .95s var(--ez) var(--d,0s) forwards}
.rv.done{animation:none;opacity:1}
@keyframes rise{from{opacity:0;transform:translateY(30px)}to{opacity:1;transform:none}}

/* ---------- HERO GRID ---------- */
.g-hero{grid-template-columns:repeat(12,1fr);grid-auto-rows:minmax(88px,auto);grid-template-areas:
 "hero hero hero hero hero hero hero med med med med med"
 "hero hero hero hero hero hero hero med med med med med"
 "tick tick tick tick tick tick tick tick tick tick tick tick"
 "st1 st1 st1 st2 st2 st2 st3 st3 st3 st4 st4 st4"
 "quo quo quo quo quo list list list list cta cta cta"
 "quo quo quo quo quo list list list list cta cta cta"
 "loc loc loc loc clk clk clk clk soc soc soc soc"}
.a-hero{grid-area:hero}.a-med{grid-area:med}.a-tick{grid-area:tick}
.a-st1{grid-area:st1}.a-st2{grid-area:st2}.a-st3{grid-area:st3}.a-st4{grid-area:st4}
.a-quo{grid-area:quo}.a-list{grid-area:list}.a-cta{grid-area:cta}
.a-loc{grid-area:loc}.a-clk{grid-area:clk}.a-soc{grid-area:soc}

.hero{display:flex;flex-direction:column;justify-content:space-between;gap:26px;padding:clamp(24px,3.4vw,48px)}
.pill{display:inline-flex;align-self:flex-start;gap:8px;align-items:center;border:var(--bd);border-radius:999px;padding:8px 14px;font-size:10px;letter-spacing:.18em;color:var(--muted);max-width:100%;overflow-wrap:anywhere}
.pill::before{content:"";width:6px;height:6px;border-radius:50%;background:var(--accent);flex:none;animation:blink 2s infinite}
@keyframes blink{50%{opacity:.25}}
.hero-title{font-family:var(--fd);font-weight:800;font-size:clamp(2rem,4.6vw,4.4rem);line-height:1.06;letter-spacing:-.01em;text-wrap:balance;overflow-wrap:break-word}
[data-theme="atelier"] .hero-title{font-weight:600;letter-spacing:-.015em}
[data-theme="roast"] .hero-title{font-weight:700}
[data-theme="volt"] .hero-title{text-transform:uppercase;letter-spacing:.01em;line-height:.98}
.hero-sub{max-width:52ch;color:var(--muted);font-size:clamp(.92rem,1.1vw,1.05rem)}
.hero-cta{display:flex;gap:14px;flex-wrap:wrap}
.hero-meta{font-size:10px;letter-spacing:.18em;color:var(--muted);overflow-wrap:anywhere}

.med{padding:0;min-height:420px}
.med img,.s-img img{width:100%;height:100%;object-fit:cover;position:absolute;inset:0;transition:opacity .55s ease,transform 1.2s var(--ez)}
img.loading{opacity:0}
.cell:hover .med img,.cell:hover.s-img img{transform:scale(1.045)}
.badge{position:absolute;top:14px;left:14px;background:var(--accent);color:var(--accent-ink);padding:7px 12px;border-radius:var(--btnr);font-size:10px;letter-spacing:.16em;z-index:2}
.cap{position:absolute;left:12px;right:12px;bottom:12px;background:color-mix(in srgb,var(--bg) 72%,transparent);backdrop-filter:blur(10px);border:var(--bd);border-radius:calc(var(--radius) - 6px);padding:10px 14px;font-size:10px;letter-spacing:.12em;color:var(--ink);z-index:2}

.tick{padding:0;display:flex;align-items:center;overflow:hidden;min-height:60px}
.mq-track{display:flex;width:max-content;animation:mq 30s linear infinite}
.tick:hover .mq-track{animation-play-state:paused}
.mq-inner{display:flex;align-items:center}
.mq-w{font-family:var(--fd);font-size:clamp(.85rem,1.4vw,1.15rem);letter-spacing:.1em;padding:0 22px;white-space:nowrap}
[data-theme="volt"] .mq-w{text-transform:uppercase}
.mq-d{color:var(--accent);font-size:.8em}
@keyframes mq{to{transform:translateX(-50%)}}

.stat{display:flex;flex-direction:column;justify-content:center;gap:6px}
.stat-v{font-family:var(--fd);font-weight:800;font-size:clamp(1.6rem,2.6vw,2.5rem);line-height:1;color:var(--ink)}
[data-theme="atelier"] .stat-v{font-weight:600}
.stat-v .suf{color:var(--accent);font-size:.62em;margin-left:2px}
.stat-l{font-size:.85rem;color:var(--ink)}
.stat-sub{font-family:var(--fm);font-size:10px;letter-spacing:.14em;color:var(--muted);text-transform:uppercase}

.quo{display:flex;flex-direction:column;justify-content:space-between;gap:22px;background:var(--card2)}
.quo-mark{font-family:var(--fd);font-size:3rem;line-height:.6;color:var(--accent)}
.quo-t{font-family:var(--fd);font-weight:500;font-size:clamp(1.05rem,1.5vw,1.35rem);line-height:1.45}
[data-theme="atelier"] .quo-t{font-style:italic;font-weight:400}
[data-theme="roast"] .quo-t{font-weight:500}
.quo footer{display:flex;flex-direction:column;gap:2px}
.quo footer b{font-size:.95rem}
.quo footer span{font-family:var(--fm);font-size:10px;letter-spacing:.14em;color:var(--muted);text-transform:uppercase}

.cell-t{font-family:var(--fd);font-weight:700;font-size:1.05rem;margin-bottom:18px}
[data-theme="atelier"] .cell-t{font-weight:600}
.a-list{display:flex;flex-direction:column}
.lst{list-style:none;display:flex;flex-direction:column}
.lst li{display:flex;gap:14px;align-items:baseline;padding:13px 0;border-top:var(--bd);font-size:.95rem}
.lst li:first-child{border-top:none;padding-top:0}
.lst li::before{content:attr(data-n);font-family:var(--fm);font-size:10px;color:var(--accent);letter-spacing:.14em;flex:none}

.cta{background:var(--accent);color:var(--accent-ink);display:flex;flex-direction:column;gap:14px;justify-content:space-between}
.cta h3{font-family:var(--fd);font-weight:800;font-size:clamp(1.3rem,1.8vw,1.7rem);line-height:1.1}
[data-theme="atelier"] .cta h3{font-weight:600}
.cta p{font-size:.9rem;opacity:.85}
.cta .btn-p{background:var(--accent-ink);color:var(--accent);align-self:flex-start}
.cta .note{font-size:10px;letter-spacing:.16em;text-transform:uppercase;opacity:.75}
.cta::after{content:"→";position:absolute;right:18px;bottom:10px;font-family:var(--fd);font-size:2.4rem;opacity:.25}

.mini{display:flex;flex-direction:column;justify-content:center;gap:4px;padding:18px 22px}
.mini .lab{font-size:9px;letter-spacing:.2em;color:var(--muted)}
.mini b{font-family:var(--fd);font-weight:700;font-size:1.1rem}
[data-theme="atelier"] .mini b{font-weight:600}
.mini .fm{font-size:10px;letter-spacing:.1em;color:var(--muted);overflow-wrap:anywhere}
.soc-row{display:flex;gap:8px;margin-top:6px}
.soc-row a{width:38px;height:38px;border:var(--bd);border-radius:calc(var(--radius) - 6px);display:grid;place-items:center;font-family:var(--fm);font-size:10px;letter-spacing:.08em;transition:background-color .3s,color .3s,transform .3s}
.soc-row a:hover{background:var(--accent);color:var(--accent-ink);transform:translateY(-3px)}

/* ---------- MANIFESTO ---------- */
.man{padding:clamp(64px,9vw,140px) 0 28px}
.eyebrow{font-size:10px;letter-spacing:.22em;color:var(--accent);text-transform:uppercase;display:block;margin-bottom:22px}
.man-t{font-family:var(--fd);font-weight:500;font-size:clamp(1.55rem,4.4vw,3.6rem);line-height:1.15;max-width:24ch;text-wrap:balance}
[data-theme="atelier"] .man-t{font-weight:400;font-style:italic}
[data-theme="volt"] .man-t{text-transform:uppercase;line-height:1.05}

/* ---------- SPECTRUM GRID ---------- */
.sec-h{display:flex;align-items:flex-end;justify-content:space-between;gap:20px;padding:40px 0 22px;flex-wrap:wrap}
.sec-t{font-family:var(--fd);font-weight:800;font-size:clamp(1.4rem,2.6vw,2.3rem)}
[data-theme="atelier"] .sec-t{font-weight:600}
[data-theme="volt"] .sec-t{text-transform:uppercase}
.sec-n{font-family:var(--fm);font-size:10px;letter-spacing:.2em;color:var(--muted)}
.g-spec{grid-template-columns:repeat(12,1fr);grid-auto-rows:minmax(96px,auto);grid-template-areas:
 "num num num img img img img img lst lst lst lst"
 "num num num img img img img img lst lst lst lst"
 "prg prg prg prg prg prg prg prt prt prt prt prt"}
.s-num{grid-area:num}.s-img{grid-area:img}.s-lst{grid-area:lst}.s-prg{grid-area:prg}.s-prt{grid-area:prt}
.s-num{background:var(--card2);display:flex;flex-direction:column;justify-content:center;gap:14px}
.num-v{font-family:var(--fd);font-weight:800;font-size:clamp(2.6rem,6vw,5.4rem);line-height:1}
[data-theme="atelier"] .num-v{font-weight:600}
.num-v .suf{color:var(--accent);font-size:.5em}
.s-num p{color:var(--muted);font-size:.9rem;max-width:26ch}
.s-img{padding:0;min-height:380px}
.chk-list{list-style:none;display:flex;flex-direction:column;gap:14px}
.chk-list li{display:flex;gap:12px;align-items:flex-start;font-size:.95rem}
.chk-list svg{flex:none;width:18px;height:18px;color:var(--accent);margin-top:2px}
.bars{display:flex;flex-direction:column;gap:18px}
.bar-l{display:flex;justify-content:space-between;gap:10px;font-size:.85rem;margin-bottom:8px}
.bar-l .fm{color:var(--accent);flex:none}
.bar{height:6px;background:var(--card2);border-radius:99px;overflow:hidden}
.bar-fill{display:block;height:100%;width:0;background:var(--accent);border-radius:99px;transition:width 1.4s var(--ez) .15s}
.prt-list{list-style:none;display:flex;flex-wrap:wrap;gap:10px}
.prt-list li{border:var(--bd);border-radius:999px;padding:9px 16px;font-family:var(--fm);font-size:10px;letter-spacing:.14em;color:var(--muted);transition:color .3s,border-color .3s,transform .3s}
.prt-list li:hover{color:var(--accent);border-color:var(--accent);transform:translateY(-3px)}

/* ---------- FOOTER ---------- */
.footer{padding:80px 28px 28px}
.f-grid{display:flex;justify-content:space-between;gap:40px;flex-wrap:wrap;align-items:flex-start;padding-bottom:40px}
.f-grid p{max-width:38ch;color:var(--muted);font-size:.92rem;margin-top:16px}
.f-links{display:flex;flex-direction:column;gap:10px;font-family:var(--fm);font-size:12px;letter-spacing:.1em}
.f-links a{color:var(--muted);transition:color .3s,transform .3s;display:inline-block}
.f-links a:hover{color:var(--accent);transform:translateX(6px)}
.giant{font-family:var(--fd);font-weight:800;font-size:clamp(3.6rem,20vw,19rem);line-height:.92;text-align:center;user-select:none;
  color:transparent;-webkit-text-stroke:1.5px var(--line);transition:-webkit-text-stroke .7s;letter-spacing:.02em;overflow-wrap:anywhere}
[data-theme="atelier"] .giant{font-weight:600}
.f-meta{display:flex;justify-content:space-between;gap:12px;flex-wrap:wrap;padding-top:22px;border-top:var(--bd);font-size:10px;letter-spacing:.16em;color:var(--muted);text-transform:uppercase}

/* ============================================================
   RESPONSIVE
   ============================================================ */
@media(max-width:1360px){
  .sw-lab{display:none}
  .hdr-in{gap:16px}
  .dnav{gap:16px}
}
@media(max-width:1120px){
  .dnav{display:none}
  .burger{display:flex}
  .hdr-in{padding:12px 20px;gap:14px}
  .g-hero{grid-template-columns:repeat(6,1fr);grid-template-areas:
   "hero hero hero hero hero hero"
   "hero hero hero hero hero hero"
   "med med med med med med"
   "med med med med med med"
   "tick tick tick tick tick tick"
   "st1 st1 st1 st2 st2 st2"
   "st3 st3 st3 st4 st4 st4"
   "quo quo quo quo quo quo"
   "list list list list list list"
   "cta cta cta cta cta cta"
   "loc loc clk clk soc soc"}
  .g-spec{grid-template-columns:repeat(6,1fr);grid-template-areas:
   "num num num num num num"
   "img img img img img img"
   "lst lst lst lst lst lst"
   "prg prg prg prg prg prg"
   "prt prt prt prt prt prt"}
}
@media(max-width:900px){
  .sw-btn span{display:none}
  .sw-btn{padding:7px 13px}
}
@media(max-width:760px){
  .wrap{padding:0 16px}
  main.wrap{padding-top:100px}
  .hdr-in{padding:10px 14px;gap:10px;min-height:58px}
  .btn-s{display:none}
  .mark{width:32px;height:32px;font-size:9px}
  .g-hero,.g-spec{grid-template-columns:1fr;grid-template-areas:none;grid-auto-rows:auto}
  .g-hero>*,.g-spec>*{grid-area:auto!important}
  .hero{gap:20px}
  .hero-cta{width:100%}
  .hero-cta .btn{flex:1 1 150px}
  .med,.s-img{min-height:0;aspect-ratio:16/11}
  .tick{min-height:52px}
  .mq-w{padding:0 14px}
  .man{padding:54px 0 22px}
  .sec-h{padding:30px 0 16px}
  .footer{padding:56px 16px 22px}
  .cell{padding:22px}
}
@media(max-width:480px){
  .brand-t{display:none}
  .sw-btn{padding:7px 10px}
  .hero-title{font-size:clamp(1.7rem,8.6vw,2.4rem)}
  .cell{padding:20px}
  .cell[data-i]::after{top:12px;right:14px}
  .med,.s-img{aspect-ratio:4/3}
  .f-grid{gap:26px}
  .f-meta{font-size:9px}
  .mnav a{padding:12px 0}
}
@media(prefers-reduced-motion:reduce){
 *,*::before,*::after{animation-duration:.01ms!important;transition-duration:.01ms!important}
 .rv{opacity:1}
}
`

// ── HTML body ─────────────────────────────────────────────────────────────────
const BENTO_LANDING_HTML_BODY = `
<div id="cur" aria-hidden="true"></div>

<!-- ================= HEADER ================= -->
<header class="hdr" id="hdr">
  <div class="hdr-in">
    <a class="brand" href="#top" aria-label="BST — на главную">
      <span class="mark">BST</span>
      <span class="brand-t fm">BST<sup>®</sup></span>
    </a>
    <nav class="dnav fm">
      <a href="#top" data-t="nav1"></a>
      <a href="#manifest" data-t="nav2"></a>
      <a href="#spectrum" data-t="nav3"></a>
      <a href="#contact" data-t="nav4"></a>
    </nav>
    <div class="hdr-r">
      <span class="sw-lab fm">4 БРЕНДА · 1 КОД</span>
      <div class="switcher" role="tablist" aria-label="Выбор бренда">
        <span class="sw-ind" id="swInd"></span>
        <button type="button" class="sw-btn on" data-set="noir"><i style="--c:#c8f542"></i><span>Noir</span></button>
        <button type="button" class="sw-btn" data-set="atelier"><i style="--c:#b0512b"></i><span>Atelier</span></button>
        <button type="button" class="sw-btn" data-set="roast"><i style="--c:#ff8a3d"></i><span>Roast</span></button>
        <button type="button" class="sw-btn" data-set="volt"><i style="--c:#e42313"></i><span>Volt</span></button>
      </div>
      <button type="button" class="btn btn-p btn-s"><span data-t="hcta"></span></button>
      <button type="button" class="burger" id="burger" aria-label="Меню"><span></span><span></span></button>
    </div>
  </div>
</header>

<nav class="mnav" id="mnav">
  <a href="#top"><span data-t="nav1"></span><small>01</small></a>
  <a href="#manifest"><span data-t="nav2"></span><small>02</small></a>
  <a href="#spectrum"><span data-t="nav3"></span><small>03</small></a>
  <a href="#contact"><span data-t="nav4"></span><small>04</small></a>
</nav>

<main class="wrap">

  <!-- ================= HERO BENTO ================= -->
  <section class="bento g-hero">
    <div class="cell a-hero hero rv" data-i="01">
      <div class="pill fm" data-t="pill"></div>
      <h1 class="hero-title" data-t="title"></h1>
      <p class="hero-sub" data-t="sub"></p>
      <div class="hero-cta">
        <button type="button" class="btn btn-p"><span data-t="btn1"></span>
          <svg width="15" height="12" viewBox="0 0 15 12" fill="none"><path d="M9 1l5 5-5 5M14 6H1" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </button>
        <button type="button" class="btn btn-g"><span data-t="btn2"></span></button>
      </div>
      <div class="hero-meta fm" data-t="meta"></div>
    </div>

    <figure class="cell a-med med rv tilt" data-i="02">
      <img data-img="img1" alt="BST — ключевой визуал бренда">
      <span class="badge fm" data-t="badge"></span>
      <figcaption class="cap fm" data-t="cap"></figcaption>
    </figure>

    <div class="cell a-tick tick rv" data-t="marq"><div class="mq-track"><div class="mq-inner"></div><div class="mq-inner"></div></div></div>

    <div class="cell a-st1 stat rv"><div class="stat-v" data-t="st1v"><span data-cnt="0"></span><span class="suf"></span></div><div class="stat-l" data-t="st1l"></div><div class="stat-sub" data-t="st1sub"></div></div>
    <div class="cell a-st2 stat rv"><div class="stat-v" data-t="st2v"><span data-cnt="0"></span><span class="suf"></span></div><div class="stat-l" data-t="st2l"></div><div class="stat-sub" data-t="st2sub"></div></div>
    <div class="cell a-st3 stat rv"><div class="stat-v" data-t="st3v"><span data-cnt="0"></span><span class="suf"></span></div><div class="stat-l" data-t="st3l"></div><div class="stat-sub" data-t="st3sub"></div></div>
    <div class="cell a-st4 stat rv"><div class="stat-v" data-t="st4v"><span data-cnt="0"></span><span class="suf"></span></div><div class="stat-l" data-t="st4l"></div><div class="stat-sub" data-t="st4sub"></div></div>

    <blockquote class="cell a-quo quo rv" data-i="05">
      <div class="quo-mark">"</div>
      <p class="quo-t" data-t="quote"></p>
      <footer><b data-t="quoteA"></b><span data-t="quoteR"></span></footer>
    </blockquote>

    <div class="cell a-list rv" data-i="06">
      <h3 class="cell-t" data-t="listT"></h3>
      <ul class="lst">
        <li data-n="01" data-t="list1"></li>
        <li data-n="02" data-t="list2"></li>
        <li data-n="03" data-t="list3"></li>
      </ul>
    </div>

    <div class="cell a-cta cta rv" data-i="07">
      <h3 data-t="ctaT"></h3>
      <p data-t="ctaX"></p>
      <button type="button" class="btn btn-p"><span data-t="ctaB"></span></button>
      <div class="fm note" data-t="ctaN"></div>
    </div>

    <div class="cell a-loc mini rv"><span class="fm lab">HQ · ГОРОД</span><b data-t="city"></b><span class="fm" data-t="coords"></span></div>
    <div class="cell a-clk mini rv"><span class="fm lab">LOCAL TIME</span><b id="clock">00:00:00</b><span class="fm">МСК · UTC+3</span></div>
    <div class="cell a-soc mini rv"><span class="fm lab">SOCIAL</span><b data-t="city2"></b>
      <div class="soc-row"><a href="#" aria-label="Telegram">TG</a><a href="#" aria-label="Behance">BE</a><a href="#" aria-label="LinkedIn">IN</a></div>
    </div>
  </section>

  <!-- ================= MANIFESTO ================= -->
  <section class="man rv" id="manifest">
    <span class="fm eyebrow">01 · Манифест</span>
    <p class="man-t" data-t="mani"></p>
  </section>

  <!-- ================= SPECTRUM BENTO ================= -->
  <section id="spectrum">
    <header class="sec-h rv">
      <div><span class="fm eyebrow" style="margin-bottom:10px">02 · Спектр</span><h2 class="sec-t" data-t="specT"></h2></div>
      <span class="sec-n">BST® / BENTO-СИСТЕМА / 12 COL</span>
    </header>
    <div class="bento g-spec">
      <div class="cell s-num rv" data-i="08">
        <div class="num-v"><span data-cnt="0" id="numCnt"></span><span class="suf" id="numSuf"></span></div>
        <p data-t="numL"></p>
      </div>
      <figure class="cell s-img rv tilt">
        <img data-img="img2" alt="BST — процесс и продукт">
        <span class="cap fm" data-t="img2cap"></span>
      </figure>
      <div class="cell s-lst rv" data-i="09">
        <h3 class="cell-t" data-t="chkT"></h3>
        <ul class="chk-list" id="chkList"></ul>
      </div>
      <div class="cell s-prg rv" data-i="10">
        <h3 class="cell-t" data-t="prgT"></h3>
        <div class="bars">
          <div class="bar-w"><div class="bar-l"><span class="bar-l-t"></span><span class="fm bar-v"></span></div><div class="bar"><span class="bar-fill"></span></div></div>
          <div class="bar-w"><div class="bar-l"><span class="bar-l-t"></span><span class="fm bar-v"></span></div><div class="bar"><span class="bar-fill"></span></div></div>
          <div class="bar-w"><div class="bar-l"><span class="bar-l-t"></span><span class="fm bar-v"></span></div><div class="bar"><span class="bar-fill"></span></div></div>
        </div>
      </div>
      <div class="cell s-prt rv" data-i="11">
        <h3 class="cell-t" data-t="prtT"></h3>
        <ul class="prt-list" id="prtList"></ul>
      </div>
    </div>
  </section>
</main>

<!-- ================= FOOTER ================= -->
<footer class="footer" id="contact">
  <div class="f-grid rv">
    <div>
      <a class="brand" href="#top"><span class="mark">BST</span><span class="brand-t fm">BST<sup>®</sup></span></a>
      <p data-t="fTag"></p>
    </div>
    <div class="f-links">
      <a href="mailto:hello@bst.studio">hello@bst.studio</a>
      <a href="#">Telegram ↗</a>
      <a href="#">Behance ↗</a>
      <a href="#">LinkedIn ↗</a>
    </div>
  </div>
  <div class="giant rv" aria-hidden="true">BST®</div>
  <div class="f-meta fm"><span>© 2025 BST® — все права защищены</span><span>4 бренда · один код · bento engine v1.0</span></div>
</footer>
`

// ── Engine JS ─────────────────────────────────────────────────────────────────
function bentoLandingEngineJs(brand: string, mode: 'canvas' | 'preview'): string {
  return `
/* ================= BST BENTO ENGINE ================= */
const IMG = {
  noir:{img1:"https://image.qwenlm.ai/public_source/0786b9fe-6ae2-4377-be81-c52e3365ed63/1bad5605c-80b7-452a-ad6e-bf52f2469183.png",
        img2:"https://image.qwenlm.ai/public_source/0786b9fe-6ae2-4377-be81-c52e3365ed63/1444e24ea-647a-4d80-bbef-2e7a09075bdc.png"},
  atelier:{img1:"https://image.qwenlm.ai/public_source/0786b9fe-6ae2-4377-be81-c52e3365ed63/181b0d0c2-eeaa-4f1a-98ea-5683514c9d00.png",
        img2:"https://image.qwenlm.ai/public_source/0786b9fe-6ae2-4377-be81-c52e3365ed63/14dc7b375-4872-4dbb-be75-afa909ed2b98.png"},
  roast:{img1:"https://image.qwenlm.ai/public_source/0786b9fe-6ae2-4377-be81-c52e3365ed63/152b6819d-2602-4754-9588-381b5aa602b7.png",
        img2:"https://image.qwenlm.ai/public_source/0786b9fe-6ae2-4377-be81-c52e3365ed63/17812ff6c-6d2b-414c-b62b-84e719450505.png"},
  volt:{img1:"https://image.qwenlm.ai/public_source/0786b9fe-6ae2-4377-be81-c52e3365ed63/1268be129-006a-4272-a0d3-795eaf522333.png",
        img2:"https://image.qwenlm.ai/public_source/0786b9fe-6ae2-4377-be81-c52e3365ed63/1ea0eb090-b59e-4605-9c2e-b09fb51e1832.png"}
};

const THEMES = {
noir:{
 label:"NOIR", lines:["Интеллект —","это <em>инфраструктура</em>"], numV:11,
 t:{nav1:"Платформа",nav2:"Манифест",nav3:"Спектр",nav4:"Контакты",hcta:"Демо",
 pill:"BST® · НЕЙРОИНФРАСТРУКТУРА · EST. 2019",
 sub:"BST проектирует вычислительные контуры для моделей, которые меняют индустрии: от обучения на тысячах GPU до инференса за миллисекунды.",
 btn1:"Запросить демо",btn2:"Смотреть манифест",meta:"MOSCOW · DUBAI · TALLINN — 55.7558° N",
 badge:"CORE X-1",cap:"Кластер BST-Норд · 4 800 GPU · H200",
 quote:"Мы перенесли обучение флагманской модели в BST и сократили цикл с девяти недель до одиннадцати дней. Это уже не вендор — это продолжение нашей инженерии.",
 quoteA:"Д. Соколов",quoteR:"CTO, «Гелион»",
 listT:"Стек платформы",list1:"Обучение и дообучение моделей",list2:"Инференс с автоскейлом и SLA",list3:"Закрытые контуры и on-premise",
 ctaT:"Готовы к разгону?",ctaX:"Первый пилот — за 14 дней. Архитекторы BST подключаются с первого звонка.",ctaB:"Запросить демо",ctaN:"NDA по умолчанию",
 city:"МОСКВА",city2:"@bst_ai",coords:"55.7558° N · 37.6173° E",
 numSuf:" дней",numL:"от брифа до пилота в продакшене — медиана 2024 года",
 specT:"Инженерия полного цикла",img2cap:"Дата-центр BST-Норд · ночная смена",
 chkT:"Что входит в пилот",prgT:"Система в реальном времени",prtT:"Нам доверяют",
 mani:"Интеллект должен течь, как ток: незаметно, бесперебойно, повсюду.",
 fTag:"BST® — инфраструктура машинного интеллекта. Проектируем, обучаем и держим SLA с 2019 года."},
 stats:[{v:99.98,dec:2,suf:"%",l:"SLA-аптайм платформы",sub:"36 месяцев подряд"},
        {v:14,suf:" мс",l:"медианная задержка инференса",sub:"p50 · глобально"},
        {v:4.8,dec:1,suf:"K",l:"GPU в активных кластерах",sub:"H200 · A100"},
        {v:120,suf:"+",l:"команд в продакшене",sub:"финтех · ритейл · био"}],
 bars:[{l:"Утилизация GPU",p:87},{l:"Энергоэффективность",p:74},{l:"Автозакрытие инцидентов",p:96}],
 check:["Аудит данных и постановка задач","Схема архитектуры и смета за 5 дней","Модель в продакшене и мониторинг","Обучение вашей команды"],
 partners:["ГЕЛИОН","AXIOM BANK","NORDRE","KVANT.LAB","РЕТЕЙЛ №1","BIOGEN X"],
 marq:["НЕЙРОСЕТИ","GPU-КЛАСТЕРЫ","MLOPS","RAG-КОНТУРЫ","БЕЗОПАСНОСТЬ","EDGE-ВЫЧИСЛЕНИЯ"]
},
atelier:{
 label:"ATELIER", lines:["Тишина,","сшитая <em>по фигуре</em>"], numV:38,
 t:{nav1:"Коллекции",nav2:"Манифест",nav3:"Ателье",nav4:"Запись",hcta:"Запись",
 pill:"BST® · ДОМ КРОЯ · ОСН. 2012",
 sub:"Капсула FW'25: двенадцать предметов из шерсти, льна и кашемира. Каждая вещь отшивается вручную в ателье BST — без сезонов и серий.",
 btn1:"Записаться на примерку",btn2:"Смотреть коллекцию",meta:"PARIS · MILANO · MOSCOW — 48.8566° N",
 badge:"FW'25",cap:"Пальто № 07 · шерсть, рогожка · ручной шов",
 quote:"Хороший крой не кричит. Он входит в комнату на секунду раньше вас — и остаётся, когда вы уходите.",
 quoteA:"Э. Боннэ",quoteR:"Креативный директор BST",
 listT:"Высокая линия",list1:"Индивидуальный крой за 10 дней",list2:"Итальянские и японские ткани",list3:"Пожизненная подгонка изделия",
 ctaT:"Примерка в ателье",ctaX:"Час с мастером, чашка чая и ткань, которая вас узнаёт. Без спешки и без сезонов.",ctaB:"Выбрать время",ctaN:"Париж · Милан · Москва",
 city:"ПАРИЖ",city2:"@bst_atelier",coords:"48.8566° N · 2.3522° E",
 numSuf:" лет",numL:"суммарного опыта мастеров дома BST — четыре поколения кроя",
 specT:"Дом, который шьёт для вас",img2cap:"Раскройная BST · Милан, утро",
 chkT:"Сервис дома",prgT:"Стандарты качества",prtT:"Дом публикуют",
 mani:"Мы шьём не одежду, а паузу — момент, когда вещь узнаёт вас.",
 fTag:"BST® — дом кроя. Двенадцать предметов в год, каждая вещь — именная."},
 stats:[{v:120,suf:" ч",l:"ручной работы на одно пальто",sub:"полный цикл"},
        {v:14,suf:"",l:"предметов в капсуле FW'25",sub:"лимит 40 экземпляров"},
        {v:2,pad:2,suf:"",l:"недели моды в год",sub:"закрытые показы"},
        {v:98,suf:"%",l:"натуральные волокна",sub:"шерсть · лён · кашемир"}],
 bars:[{l:"Ручные операции",p:82},{l:"Натуральные волокна",p:98},{l:"Локальное производство",p:100}],
 check:["Снятие 24 мерок за одну встречу","Три примерки с мастером","Именная бирка и архив лекал"],
 partners:["VOGUE ITALIA","L'OFFICIEL","ELITE FABRICS","CASHMERE HOUSE","ARTEX MILANO","SALON DOUZE"],
 marq:["КАШЕМИР","ЛЁН","ШЁЛК","РУЧНАЯ РАБОТА","ПАРИЖ","МИЛАН"]
},
roast:{
 label:"ROAST", lines:["Обжарено сегодня.","Выпито <em>с любовью</em>"], numV:214,
 t:{nav1:"Зерно",nav2:"Манифест",nav3:"Обжарка",nav4:"Кофейни",hcta:"Зерно",
 pill:"BST® · СПЕШЕЛТИ-ОБЖАРКА · С 2016",
 sub:"Спешелти-зерно с 27 ферм, обжарка малыми батчами и доставка за 48 часов. Кофе, ради которого хочется вставать раньше.",
 btn1:"Выбрать зерно",btn2:"Рецепты заваривания",meta:"МОСКВА · ПИТЕР · КАЗАНЬ — 55.7558° N",
 badge:"BATCH № 214",cap:"Эфиопия Сидамо · светлая обжарка · анаэробика",
 quote:"Утро города начинается не с будильника. Оно начинается с запаха нашей обжарки на соседней улице.",
 quoteA:"М. Крылова",quoteR:"Основатель BST Roast",
 listT:"В каждой пачке",list1:"Дата обжарки на упаковке",list2:"Профиль вкуса и имя фермы",list3:"Рецепт заваривания внутри",
 ctaT:"Подписка на свежесть",ctaX:"Подберём зерно под ваш способ заваривания и привезём в течение двух дней после батча.",ctaB:"Оформить подписку",ctaN:"Первый батч −15%",
 city:"МОСКВА",city2:"@bst_roast",coords:"55.7558° N · 37.6173° E",
 numSuf:"",numL:"батчей обжарено в этом сезоне — каждый подписан вручную",
 specT:"От зерна до чашки",img2cap:"Цех обжарки BST · 06:40 утра",
 chkT:"Форматы",prgT:"Наш контроль",prtT:"С нами варят",
 mani:"Кофе — повод замедлиться. Мы обжариваем его так, чтобы повод был весомым.",
 fTag:"BST® Roast — спешелти-обжарка малыми батчами. Москва, с 2016 года."},
 stats:[{v:27,suf:"",l:"ферм-партнёров",sub:"прямые контракты"},
        {v:48,suf:" ч",l:"от обжарки до вашей чашки",sub:"доставка по городу"},
        {v:86,suf:"+",l:"баллов по шкале SCA",sub:"каждый лот"},
        {v:14,suf:"",l:"сортов в сезонной линейке",sub:"обновление раз в квартал"}],
 bars:[{l:"Свежесть партии",p:97},{l:"Прямые закупки",p:88},{l:"Повторные заказы",p:84}],
 check:["Зерно 250 г и 1 кг","Дрип-пакеты для офиса","Купажи под вашу кофейню"],
 partners:["BREW BAR","ДЕПО.УТРО","КОФЕ ДОМА","CUP&CO","ЛОКАВОР","ТЁПЛЫЙ ЦЕХ"],
 marq:["СВЕЖАЯ ОБЖАРКА","СПЕШЕЛТИ","V60","АЭРОПРЕСС","ДРИП","КАППИНГ"]
},
volt:{
 label:"VOLT", lines:["БЫСТРЕЕ","<em>ВЧЕРАШНЕГО</em> СЕБЯ"], numV:198,
 t:{nav1:"Экипировка",nav2:"Манифест",nav3:"Атлеты",nav4:"Старт",hcta:"Старт",
 pill:"BST® · PERFORMANCE GEAR",
 sub:"Карбоновые платформы, компрессия нового поколения и аэродинамика, проверенная в трубе. BST создан для тех, кто считает секунды.",
 btn1:"Начать тренировку",btn2:"Технологии BST",meta:"BERLIN · PRAHA · WIEN — 52.5200° N",
 badge:"VLT-03",cap:"Карбоновая платформа VOLT V3 · 198 г",
 quote:"На 38-м километре марафона остаётся только то, что ты тренировал. BST просто убирает всё лишнее.",
 quoteA:"А. Рой",quoteR:"Марафон · личный рекорд 2:07:41",
 listT:"Что даёт платформа V3",list1:"Возврат энергии — 87%",list2:"Компрессия без потери кровообращения",list3:"Вес 198 г в размере 42",
 ctaT:"Тест-драйв 30 дней",ctaX:"Пробеги в них месяц. Не побил личный рекорд — вернём деньги. Честно и без анкет.",ctaB:"Выбрать модель",ctaN:"Доставка за 24 часа",
 city:"БЕРЛИН",city2:"@bst_volt",coords:"52.5200° N · 13.4050° E",
 numSuf:" г",numL:"вес флагманской модели V3 — легче вашего телефона",
 specT:"Инженерия рекордов",img2cap:"VOLT V3 · карбоновая пластина в разрезе",
 chkT:"В комплекте",prgT:"Лаборатория BST",prtT:"В экипировке BST",
 mani:"Секунды не дарят — их берут. BST — инструмент, которым их берут.",
 fTag:"BST® Performance — экипировка для тех, кто считает секунды. Лаборатория в Берлине."},
 stats:[{v:0.19,dec:2,suf:" с",l:"отклик карбоновой пластины",sub:"лабораторный тест"},
        {v:3,suf:"×",l:"легче стандартной экипировки",sub:"по материалам"},
        {v:140,suf:"+",l:"про-атлетов в команде",sub:"22 страны"},
        {v:12,suf:"",l:"национальных рекордов",sub:"в экипировке BST"}],
 bars:[{l:"Возврат энергии",p:87},{l:"Износостойкость подошвы",p:92},{l:"Отвод влаги",p:95}],
 check:["3D-сканирование стопы","План тренировок от коучей BST","Гарантия 2 года на платформу"],
 partners:["TEAM IRON","SPRINT LAB","MARATHON PRO","ARENA 9","VELO CLUB","NORD RUN"],
 marq:["СКОРОСТЬ","КАРБОН","КОМПРЕССИЯ","АЭРОДИНАМИКА","ВОССТАНОВЛЕНИЕ","РЕКОРД"]
}};

const ORDER=["noir","atelier","roast","volt"];
const $=s=>document.querySelector(s), $$=s=>[...document.querySelectorAll(s)];

/* ---------- counters ---------- */
function runCounters(){
  $$("[data-cnt]").forEach(el=>{
    const target=parseFloat(el.dataset.cnt||"0"), dec=+(el.dataset.dec||0), pad=+(el.dataset.pad||0), t0=performance.now(), dur=1500;
    const fmt=v=>{let s=v.toFixed(dec); if(pad){const p=s.split("."); s=p[0].padStart(pad,"0")+(p[1]?"."+p[1]:"");} return s;};
    (function step(t){const p=Math.min(1,(t-t0)/dur), e=1-Math.pow(1-p,3);
      el.textContent=fmt(target*e); if(p<1)requestAnimationFrame(step);})(t0);
  });
}

/* ---------- theme apply ---------- */
function applyTheme(k){
  const T=THEMES[k];
  document.documentElement.dataset.theme=k;
  document.title="BST® — "+T.label+" · BENTO";
  for(const key in T.t){ $$('[data-t="'+key+'"]').forEach(n=>n.innerHTML=T.t[key]); }
  $('[data-t="title"]').innerHTML=T.lines.join("<br>");
  $$(".stat").forEach((s,i)=>{const st=T.stats[i]; if(!st)return;
    const v=s.querySelector("[data-cnt]"); v.dataset.cnt=st.v; v.dataset.dec=st.dec||0; v.dataset.pad=st.pad||0;
    s.querySelector(".suf").textContent=st.suf||""; s.querySelector(".stat-l").textContent=st.l; s.querySelector(".stat-sub").textContent=st.sub||"";});
  $("#numCnt").dataset.cnt=T.numV; $("#numSuf").textContent=T.numSuf||"";
  $$(".bar-w").forEach((b,i)=>{const br=T.bars[i]; if(!br)return;
    b.querySelector(".bar-l-t").textContent=br.l; b.querySelector(".bar-v").textContent=br.p+"%";
    const f=b.querySelector(".bar-fill"); f.style.width="0%";
    requestAnimationFrame(()=>requestAnimationFrame(()=>f.style.width=br.p+"%"));});
  $("#chkList").innerHTML=T.check.map(c=>'<li><svg viewBox="0 0 24 24"><path d="M20 6L9 17l-5-5" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"/></svg><span>'+c+"</span></li>").join("");
  $("#prtList").innerHTML=T.partners.map(p=>"<li>"+p+"</li>").join("");
  const chunk=T.marq.map(w=>'<span class="mq-w">'+w+'</span><span class="mq-d">✦</span>').join("");
  $$(".mq-inner").forEach(m=>m.innerHTML=chunk.repeat(4));
  $$("img[data-img]").forEach(im=>{const src=IMG[k][im.dataset.img]; im.classList.add("loading");
    const pre=new Image(); pre.onload=()=>{im.src=src; im.classList.remove("loading");}; pre.src=src;});
  const i=ORDER.indexOf(k);
  $("#swInd").style.transform="translateX("+(i*100)+"%)";
  $$(".sw-btn").forEach(b=>b.classList.toggle("on",b.dataset.set===k));
  runCounters();
}

/* ---------- reveal ---------- */
const io=new IntersectionObserver(es=>es.forEach(e=>{ if(e.isIntersecting){e.target.classList.add("in"); io.unobserve(e.target);} }),{threshold:.12});
$$(".rv").forEach((el,i)=>{el.style.setProperty("--d",(i%9)*0.06+"s"); el.addEventListener("animationend",()=>el.classList.add("done"),{once:true}); io.observe(el);});

/* ---------- clock (МСК) ---------- */
function tickClock(){const d=new Date(),u=d.getTime()+d.getTimezoneOffset()*60000,m=new Date(u+3*3600000);
  $("#clock").textContent=[m.getHours(),m.getMinutes(),m.getSeconds()].map(x=>String(x).padStart(2,"0")).join(":");}
setInterval(tickClock,1000); tickClock();

/* ---------- header scroll state ---------- */
const hdr=$("#hdr");
function onScroll(){hdr.classList.toggle("scrolled",window.scrollY>14);}
addEventListener("scroll",onScroll,{passive:true}); onScroll();

/* ---------- tilt ---------- */
if(matchMedia("(pointer:fine)").matches){
  $$(".tilt").forEach(c=>{c.addEventListener("mousemove",e=>{const r=c.getBoundingClientRect(),
    x=(e.clientX-r.left)/r.width-.5, y=(e.clientY-r.top)/r.height-.5;
    c.style.transform="perspective(900px) rotateY("+(x*5)+"deg) rotateX("+(-y*5)+"deg)";});
    c.addEventListener("mouseleave",()=>c.style.transform="");});
}

/* ---------- cursor ---------- */
const cur=$("#cur"); let cx=0,cy=0,tx=0,ty=0;
addEventListener("mousemove",e=>{tx=e.clientX;ty=e.clientY;cur.classList.add("on");});
document.documentElement.addEventListener("mouseleave",()=>cur.classList.remove("on"));
(function loop(){cx+=(tx-cx)*.18;cy+=(ty-cy)*.18;cur.style.left=cx+"px";cur.style.top=cy+"px";requestAnimationFrame(loop);})();
$$("a,button,.cell").forEach(el=>{el.addEventListener("mouseenter",()=>cur.classList.add("big"));el.addEventListener("mouseleave",()=>cur.classList.remove("big"));});

/* ---------- switcher / burger ---------- */
$$(".sw-btn").forEach(b=>b.addEventListener("click",()=>applyTheme(b.dataset.set)));
$("#burger").addEventListener("click",()=>document.body.classList.toggle("menu-open"));
$$("#mnav a").forEach(a=>a.addEventListener("click",()=>document.body.classList.remove("menu-open")));

/* ---------- preload + init ---------- */
Object.values(IMG).forEach(o=>Object.values(o).forEach(u=>{const i=new Image();i.src=u;}));
applyTheme("${brand}");
${mode === 'canvas' ? `(function(){
  document.querySelectorAll('.rv,.reveal,[data-rv]').forEach(function(el){el.classList.add('in','on');});
  document.querySelectorAll('.lm').forEach(function(el){el.style.clipPath='none';el.style.transform='none';el.style.opacity='1';});
  document.body.classList.add('loaded','is-ready');
  document.addEventListener('click',function(e){var a=e.target.closest('a[href]');if(a)e.preventDefault();},true);
  document.querySelectorAll('form').forEach(function(f){f.addEventListener('submit',function(e){e.preventDefault();});});
})();` : ''}
`
}

// ── Public API ────────────────────────────────────────────────────────────────
export function getBentoLandingPageHtml(data: BentoLandingPageData, mode: 'canvas' | 'preview'): string {
  const brand = data.brand ?? 'noir'
  return `<!DOCTYPE html>
<html lang="ru" data-theme="${brand}">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>BST® — NOIR · BENTO</title>
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/unbounded/500.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/unbounded/800.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/space-grotesk/400.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/space-grotesk/500.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/space-grotesk/700.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/ibm-plex-mono/400.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/ibm-plex-mono/500.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/fraunces/300.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/fraunces/400.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/fraunces/600.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/fraunces/400-italic.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/fraunces/600-italic.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/bricolage-grotesque/500.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/bricolage-grotesque/700.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/anton/400.css">
<style>${BENTO_LANDING_CSS}</style>
<style>.sw-lab,.switcher{display:none!important}</style>
</head>
<body id="top">
${BENTO_LANDING_HTML_BODY}
<script>${bentoLandingEngineJs(brand, mode)}</script>
</body>
</html>`
}
